import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import { CODE_TO_NAME_FRESH, DEFAULT_PRICING_MODEL } from './constants';
import { Option, TangoTeamItem, TangoTeamPriceModel, TangoTeamProduct } from './types';
import { getProductPricingAndNutrientInfo } from '../api/getProductPricingAndNutrientInfo';
import { Dropdown } from '../component/Dropdown';
import { MultiSelectDropdown } from '../component/MultiSelectDropdown';
import { TableProductInfo } from '../component/TableProductInfo';
import { FreshProductInfoHeaders } from '../tableHeaders';
import { BOTTOM_TOAST, createRowClickHandler, generateErrorMsg } from '../utils';

const FRESH_PRODUCT_TYPE_ORDER = ['SAMPLE', 'MEAL'];

export const FreshProductInfo = () => {
  const [freshProduct, setFreshProduct] = useState<TangoTeamProduct[]>();
  const [loading, setLoading] = useState(true);

  const [selectedPlanSize, setSelectedPlanSize] = useState<Option[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<Option[]>([]);
  const [selectedPricingModel, setSelectedPricingModel] = useState<Option>({
    label: DEFAULT_PRICING_MODEL,
    value: DEFAULT_PRICING_MODEL,
  });
  const [selectedFrequency, setSelectedFrequency] = useState<Option[]>([]);
  const [highlightedRows, setHighlightedRows] = useState<Set<number>>(new Set());
  const [lastSelectedRow, setLastSelectedRow] = useState<number | null>(null);

  useEffect(() => {
    getProductPricingAndNutrientInfo({ meal_type: 'FRESH' })
      .then((res) => {
        setFreshProduct(res.data);
      })
      .catch((err) => {
        toast.error(generateErrorMsg(err), BOTTOM_TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const { planSizeOptions, recipeOptions, pricingModelOptions, frequencyOptions } = useMemo(() => {
    if (!freshProduct) {
      return {
        planSizeOptions: [],
        recipeOptions: [],
        pricingModelOptions: [],
        frequencyOptions: [],
      };
    }

    const planSizeSet = new Set<string>();
    const pricingModelSet = new Set<string>();
    const frequencySet = new Set<string>();

    for (const product of freshProduct) {
      const planId = product?.plan_size?.id;

      if (planId) {
        planSizeSet.add(String(planId));
        frequencySet.add(String(product.frequency));
        for (const price of Object.values(product.prices as Record<string, TangoTeamPriceModel>)) {
          if (price?.pricing_model_name) {
            pricingModelSet.add(String(price.pricing_model_name));
          }
        }
      }
    }

    const planSizeOptions = Array.from(planSizeSet)
      .map((v) => ({ label: v, value: v }))
      .sort((a, b) => Number(a.value) - Number(b.value));

    const recipeOptions = Object.keys(CODE_TO_NAME_FRESH).map((key) => ({
      label: key,
      value: key,
    }));
    const pricingModelOptions = Array.from(pricingModelSet).map((v) => ({ label: v, value: v }));
    const frequencyOptions = Array.from(frequencySet)
      .map((v) => ({ label: v, value: v }))
      .sort((a, b) => Number(a.value) - Number(b.value));
    return { planSizeOptions, recipeOptions, pricingModelOptions, frequencyOptions };
  }, [freshProduct]);

  const filteredProducts = useMemo(() => {
    if (!freshProduct) return [];

    const filtered = freshProduct.filter((product: TangoTeamProduct) => {
      const planSizeMatch =
        !selectedPlanSize.length ||
        selectedPlanSize.some((option) => product?.plan_size?.id.toString() === option.value);

      const recipeMatch =
        !selectedRecipe.length ||
        selectedRecipe.some((option) =>
          product.item_info.some((item) => item.recipe_id === option.value)
        );

      const frequencyMatch =
        !selectedFrequency.length ||
        selectedFrequency.some((option) => String(product.frequency) === option.value);

      return planSizeMatch && recipeMatch && frequencyMatch;
    });

    return filtered.sort((a, b) => {
      const aTypeIndex = FRESH_PRODUCT_TYPE_ORDER.indexOf(a.type);
      const bTypeIndex = FRESH_PRODUCT_TYPE_ORDER.indexOf(b.type);
      if (aTypeIndex !== bTypeIndex) {
        if (aTypeIndex === -1) return 1;
        if (bTypeIndex === -1) return -1;
        return aTypeIndex - bTypeIndex;
      }
      return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
    });
  }, [freshProduct, selectedPlanSize, selectedRecipe, selectedFrequency]);

  const clearFilters = () => {
    setSelectedPlanSize([]);
    setSelectedRecipe([]);
    setSelectedPricingModel({
      label: DEFAULT_PRICING_MODEL,
      value: DEFAULT_PRICING_MODEL,
    });
    setSelectedFrequency([]);
  };

  const handleRowClick = createRowClickHandler(
    setHighlightedRows,
    setLastSelectedRow,
    lastSelectedRow,
    highlightedRows
  );

  const calculateBagInfo = (itemInfo: TangoTeamItem[]) => {
    let no_of_bags = 0;
    const packout_definition = [];
    const bag_weight_oz = [];

    for (const item of itemInfo) {
      no_of_bags += item.quantity;
      const skuParts = item.sku.split('-');
      if (skuParts.length < 3) {
        toast.error(
          'FreshProductInfo:calculateBagInfo item doesnt have fill amount' + item.sku,
          BOTTOM_TOAST
        );
        continue;
      }
      packout_definition.push(item.sku + ' X ' + item.quantity);
      bag_weight_oz.push(item.weight_oz + ': ' + item.recipe_id);
    }
    return {
      no_of_bags,
      packout_definition,
      bag_weight_oz,
    };
  };

  const firstHeaderGroups = [
    { label: '', colSpan: 7, className: 'bg-slate50' },
    { label: 'Packout Details', colSpan: 1, className: 'bg-amber-100 font-semibold' },
    { label: 'Pricing', colSpan: 3, className: 'bg-green-200 font-semibold' },
    { label: 'Plan Details (Fresh)', colSpan: 5, className: 'bg-purple-200 font-semibold' },
  ];

  const transformProductToTableRow = (product: TangoTeamProduct) => {
    const bagInfo = calculateBagInfo(product.item_info);
    const prices = product?.prices?.[selectedPricingModel.value];
    let noOfPacksPerDay = bagInfo.no_of_bags / (product.frequency * 7);
    let calories_per_pack = (product?.plan_size?.id ?? 0) / noOfPacksPerDay;
    if (!isFinite(noOfPacksPerDay)) {
      noOfPacksPerDay = 0;
      calories_per_pack = 0;
    }

    const currency = prices?.currency ? `${prices.currency} $` : '';

    return {
      Code: product.code,
      Description: product.description_bark,
      Type: product.type,
      Recurring: product.recurring.toString(),
      Frequency: product.frequency,
      'Plan Size': product?.plan_size?.id ?? '',
      Status: product.status,
      'PACKOUT DEFINITION': bagInfo.packout_definition,
      'Price (per DAY)': currency + (prices?.price_per_day ?? '0.00'),
      'Price (per WEEK)': currency + (prices?.price_per_week ?? '0.00'),
      'Price (per SHIPMENT)': currency + (prices?.price ?? '0.00'),
      'OZ (per pack)': bagInfo.bag_weight_oz,
      'CALORIES (per pack)': calories_per_pack,
      '# of PACKS (per shipment)': bagInfo.no_of_bags,
      '# of PACKS (fed per day)': noOfPacksPerDay,
      '# of PACKS (fed per meal)': noOfPacksPerDay / 2,
    };
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-10 flex-shrink-0">
        <h1>Fresh Product Info</h1>
      </div>

      {!loading && (
        <div className="px-10 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2">Plan Size</label>
                <MultiSelectDropdown
                  options={planSizeOptions}
                  selectedOptions={selectedPlanSize}
                  setSelectedOptions={setSelectedPlanSize}
                  placeholder="Select Plan Sizes"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2">Recipe</label>
                <MultiSelectDropdown
                  options={recipeOptions}
                  selectedOptions={selectedRecipe}
                  setSelectedOptions={setSelectedRecipe}
                  placeholder="Select Recipes"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2">Frequency</label>
                <MultiSelectDropdown
                  options={frequencyOptions}
                  selectedOptions={selectedFrequency}
                  setSelectedOptions={setSelectedFrequency}
                  placeholder="Select Frequency"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Pricing Model</label>
                <Dropdown
                  options={pricingModelOptions}
                  selectedOption={selectedPricingModel}
                  setSelectedOption={setSelectedPricingModel}
                  defaultText="Select Pricing Model"
                />
              </div>
              <div className="md:col-span-1">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 underline transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredProducts.length} of {freshProduct?.length || 0} products
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <span className="ml-2">Loading...</span>
        </div>
      ) : (
        <div className="px-10 flex-1 flex flex-col">
          <div className="flex-1 border border-gray-300 rounded-lg">
            <TableProductInfo
              header={FreshProductInfoHeaders}
              data={filteredProducts.map((product: TangoTeamProduct) =>
                transformProductToTableRow(product)
              )}
              topHeaderGroups={firstHeaderGroups}
              highlightedRows={highlightedRows}
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};
